import type { Holding, PlatformSummary, Quote, ValuedHolding } from '@porttang/core';
import { Link } from 'react-router-dom';
import { Chevron, useCollapsedGroups } from '../../shared/collapsible';
import {
  EM_DASH,
  formatPercent,
  formatPrice,
  formatQuantity,
  formatSignedTHB,
  formatTHB,
  toneClassFor,
} from '../../shared/format';
import { AssetTag, Card, SectionLabel, tintFor } from '../../shared/pixel';
import { isUnvalued } from './PlatformTabs';

/**
 * ป้ายบอกความสดของราคา — ใช้ร่วมกันทั้งตาราง (จอกว้าง) และการ์ด (จอเล็ก)
 *
 * สองป้ายนี้ไม่ขึ้นพร้อมกัน เพราะรายการที่ยังไม่มีราคาเลยจะบอกว่า "เก่า" ไม่ได้
 */
function PriceBadges({ valuation }: { valuation: ValuedHolding['valuation'] }) {
  if (valuation.quoteMissing) {
    return (
      <span
        title="ยังไม่มีราคาของรายการนี้ — กดรีเฟรชราคา"
        className="shrink-0 rounded bg-warn/15 px-1 py-0.5 text-[9.5px] whitespace-nowrap text-warn"
      >
        ไม่มีราคา
      </span>
    );
  }

  if (valuation.isStale) {
    return (
      <span
        title="ราคาเก่ากว่าที่ควร — กดรีเฟรชเพื่อดึงใหม่"
        className="shrink-0 rounded bg-line-strong px-1 py-0.5 text-[9.5px] whitespace-nowrap text-ink-400"
      >
        ราคาเก่า
      </span>
    );
  }

  return null;
}

interface Group {
  summary: PlatformSummary;
  rows: ValuedHolding[];
}

/** ตัวเลขบนหัว section — ตรงคอลัมน์มูลค่ากับกำไร/ขาดทุนของแถวข้างใต้ */
function groupValue(summary: PlatformSummary): string {
  return isUnvalued(summary) ? EM_DASH : formatTHB(summary.totals.valueTHB);
}

function groupTone(summary: PlatformSummary): string {
  return isUnvalued(summary) ? 'text-ink-400' : toneClassFor(summary.invested.pnlTHB);
}

/**
 * ตารางสินทรัพย์บนแดชบอร์ด
 *
 * `platforms` มีค่า = แบ่ง section ตามแพลตฟอร์ม (ลำดับเดียวกับแท็บ) กดหัว section เพื่อพับ/กาง
 * จอกว้างยังเป็นตารางเดียวแล้วแบ่งด้วย `<tbody>` ต่อแพลตฟอร์ม — ถ้าแยกเป็นหลายตาราง
 * ความกว้างคอลัมน์ของแต่ละ section จะคำนวณแยกกัน ตัวเลขข้าม section จะไม่ตรงแนว
 */
export function HoldingsCard({
  rows,
  quotes,
  isLoadingPrices,
  platforms,
  showPlatformName,
}: {
  rows: readonly ValuedHolding[];
  quotes: ReadonlyMap<string, Quote>;
  isLoadingPrices: boolean;
  platforms: readonly PlatformSummary[] | null;
  /** บรรทัดรองใต้สัญลักษณ์: ชื่อแพลตฟอร์ม หรือชื่อเต็ม — ในแท็บพอร์ตแยกและใน section รู้แพลตฟอร์มอยู่แล้ว */
  showPlatformName: boolean;
}) {
  const { isOpen, toggle } = useCollapsedGroups();

  // `rows` เรียงตามมูลค่ามาแล้ว — filter คงลำดับนั้นไว้ในแต่ละ section
  const groups: Group[] | null =
    platforms === null
      ? null
      : platforms.map((summary) => ({
          summary,
          rows: rows.filter(({ holding }) => holding.platform === summary.platform),
        }));

  const subtitleOf = (holding: Holding) =>
    showPlatformName && groups === null ? holding.platform : holding.displayName;

  /** null = ราคายังโหลดอยู่ — ยังไม่รู้ ต่างจากรู้แล้วว่าไม่มีราคา */
  const priceOf = (holding: Holding): string | null => {
    const quote = quotes.get(holding.priceKey);
    return isLoadingPrices && quote === undefined
      ? null
      : formatPrice(quote?.price ?? null, quote?.currency ?? null);
  };

  const renderTableRow = ({ holding, valuation }: ValuedHolding) => (
    <tr
      key={holding.id}
      className="border-b border-line-soft text-ink-100 transition last:border-0 hover:bg-hover"
    >
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-3">
          <AssetTag symbol={holding.symbol} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{holding.symbol}</span>
              <PriceBadges valuation={valuation} />
            </div>
            <div className="font-mono text-[11px] text-ink-500">{subtitleOf(holding)}</div>
          </div>
        </div>
      </td>
      <td className="px-5 py-3.5 text-right font-mono text-[13px] text-ink-200">
        {formatQuantity(holding.quantity)}
      </td>
      <td className="px-5 py-3.5 text-right font-mono text-[13px] text-ink-300">
        {priceOf(holding) ?? EM_DASH}
      </td>
      <td className="px-5 py-3.5 text-right font-mono text-[13.5px]">
        {formatTHB(valuation.marketValueTHB)}
      </td>
      <td
        className={`px-5 py-3.5 text-right font-mono text-[13px] ${toneClassFor(valuation.pnlTHB)}`}
      >
        <div>{formatSignedTHB(valuation.pnlTHB)}</div>
        {valuation.pnlPct !== null && (
          <div className="text-[11px] opacity-80">{formatPercent(valuation.pnlPct)}</div>
        )}
      </td>
    </tr>
  );

  const renderListRow = ({ holding, valuation }: ValuedHolding) => {
    const price = priceOf(holding);

    return (
      <li key={holding.id} className="flex items-center gap-1.5 px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <AssetTag symbol={holding.symbol} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-ink-100">{holding.symbol}</span>
              <PriceBadges valuation={valuation} />
            </div>
            <div className="truncate font-mono text-[10px] text-ink-500">{subtitleOf(holding)}</div>
          </div>
        </div>

        <div className="w-[110px] shrink-0 text-right">
          <div className="font-mono text-[14px] font-semibold text-ink-100">
            {formatTHB(valuation.marketValueTHB)}
          </div>
          {/* จำนวนคูณราคา = มูลค่าข้างบน — วางต่อกันให้เห็นว่าเลขนั้นมาจากไหน */}
          <div className="truncate font-mono text-[10px] text-ink-500">
            {formatQuantity(holding.quantity)}
            {price !== null && price !== EM_DASH && ` × ${price}`}
          </div>
        </div>

        <div className={`w-[70px] shrink-0 text-right ${toneClassFor(valuation.pnlTHB)}`}>
          <div className="font-mono text-[14px] font-semibold">
            {formatPercent(valuation.pnlPct)}
          </div>
          <div className="font-mono text-[10px] opacity-80">
            {formatSignedTHB(valuation.pnlTHB)}
          </div>
        </div>
      </li>
    );
  };

  return (
    <Card className="overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <SectionLabel>HOLDINGS · {rows.length} ITEMS</SectionLabel>
        <Link to="/holdings" className="text-[13px] text-ink-400 transition hover:text-brand-text">
          จัดการสินทรัพย์ →
        </Link>
      </header>

      {/* จอกว้าง: ตาราง */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line-soft text-left font-mono text-[10.5px] tracking-[0.5px] text-ink-500">
              <th className="px-5 py-3 font-normal">ASSET</th>
              <th className="px-5 py-3 text-right font-normal">จำนวน</th>
              <th className="px-5 py-3 text-right font-normal">ราคา</th>
              <th className="px-5 py-3 text-right font-normal">มูลค่า</th>
              <th className="px-5 py-3 text-right font-normal">กำไร/ขาดทุน</th>
            </tr>
          </thead>

          {groups === null ? (
            <tbody>{rows.map(renderTableRow)}</tbody>
          ) : (
            groups.map(({ summary, rows: groupRows }) => {
              const open = isOpen(summary.platform);
              return (
                <tbody key={summary.platform}>
                  <tr className="border-y border-line bg-sunken/60 transition hover:bg-hover">
                    <td colSpan={3} className="p-0">
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => toggle(summary.platform)}
                        className="flex w-full items-center gap-2.5 px-5 py-2.5 text-left"
                      >
                        <Chevron open={open} />
                        <span
                          aria-hidden
                          className="size-2.5 shrink-0"
                          style={{ backgroundColor: tintFor(summary.platform) }}
                        />
                        <span className="font-semibold text-ink-100">{summary.platform}</span>
                        <span className="font-mono text-[11px] text-ink-500">
                          {summary.totals.holdingCount} รายการ
                        </span>
                      </button>
                    </td>
                    <td className="px-5 py-2.5 text-right font-mono text-[13px] text-ink-200">
                      {groupValue(summary)}
                    </td>
                    <td
                      className={`px-5 py-2.5 text-right font-mono text-[12px] ${groupTone(summary)}`}
                    >
                      {formatPercent(summary.invested.pnlPct)}
                    </td>
                  </tr>
                  {open && groupRows.map(renderTableRow)}
                </tbody>
              );
            })
          )}
        </table>
      </div>

      {/*
        จอเล็ก: สามคอลัมน์ — ชื่อ / มูลค่า / กำไรขาดทุน

        ตาราง 5 คอลัมน์บนมือถือต้องเลื่อนแนวนอนจนมูลค่ากับกำไรตกขอบจอ แต่การยุบเป็น
        การ์ดอิสระก็อ่านยากพอกัน เพราะตัวเลขแต่ละแถวไม่ตรงคอลัมน์กัน ตาต้องไล่หาใหม่ทุกแถว

        จึงล็อกความกว้างสองคอลัมน์ขวาไว้ตายตัว ตัวเลขทุกแถวเลยชิดขวาตรงแนวเดียวกัน
        กวาดสายตาลงมาเทียบกันได้ทันที ส่วนจำนวนกับราคาลดชั้นลงเป็นบรรทัดรองใต้มูลค่า
        และมีหัวคอลัมน์กำกับไว้ข้างบน จะได้ไม่ต้องเขียนป้ายชื่อซ้ำในทุกแถว (spec Section 9.3)
        หัว section ใช้ความกว้างคอลัมน์เดียวกัน ยอดรวมของแพลตฟอร์มจึงตรงแนวกับแถวข้างใต้
      */}
      <div className="sm:hidden">
        <div className="flex items-end gap-1.5 border-b border-line-soft px-4 pb-2 font-mono text-[10.5px] tracking-[0.5px] text-ink-500">
          <span className="min-w-0 flex-1">{rows.length} สินทรัพย์</span>
          <span className="w-[110px] text-right">มูลค่า (THB)</span>
          <span className="w-[70px] text-right">กำไร/ขาดทุน</span>
        </div>

        {groups === null ? (
          <ul className="divide-y divide-line-soft">{rows.map(renderListRow)}</ul>
        ) : (
          groups.map(({ summary, rows: groupRows }) => {
            const open = isOpen(summary.platform);
            return (
              <section key={summary.platform} className="border-b border-line last:border-0">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => toggle(summary.platform)}
                  className="flex w-full items-center gap-1.5 bg-sunken/60 px-4 py-2.5 text-left"
                >
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    <Chevron open={open} />
                    <span
                      aria-hidden
                      className="size-2.5 shrink-0"
                      style={{ backgroundColor: tintFor(summary.platform) }}
                    />
                    <span className="truncate text-[13px] font-semibold text-ink-100">
                      {summary.platform}
                    </span>
                    <span className="shrink-0 font-mono text-[10px] text-ink-500">
                      {summary.totals.holdingCount}
                    </span>
                  </span>
                  <span className="w-[110px] shrink-0 text-right font-mono text-[13px] text-ink-200">
                    {groupValue(summary)}
                  </span>
                  <span
                    className={`w-[70px] shrink-0 text-right font-mono text-[12px] ${groupTone(summary)}`}
                  >
                    {formatPercent(summary.invested.pnlPct)}
                  </span>
                </button>
                {open && (
                  <ul className="divide-y divide-line-soft border-t border-line-soft">
                    {groupRows.map(renderListRow)}
                  </ul>
                )}
              </section>
            );
          })
        )}
      </div>
    </Card>
  );
}
